/**
 * Emergency Theme Bypass System
 * This completely replaces the Material-UI theme system to avoid emotion's attachTheme
 */

interface ThemeConfig {
  palette?: {
    mode?: string;
    primary?: {
      main?: string;
      light?: string;
      dark?: string;
    };
    secondary?: {
      main?: string;
      light?: string;
      dark?: string;
    };
  };
  customization?: any;
}

interface BypassTheme {
  palette: {
    mode: string;
    primary: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    secondary: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    error: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    warning: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    success: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    info: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    background: {
      default: string;
      paper: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
    divider: string;
    action: {
      hover: string;
      selected: string;
    };
    grey: {
      [key: number]: string;
    };
  };
  spacing: (factor: number) => string;
  shape: {
    borderRadius: number;
  };
  breakpoints: {
    keys: string[];
    values: {
      [key: string]: number;
    };
    up: (key: string) => string;
    down: (key: string) => string;
    between: (start: string, end: string) => string;
    only: (key: string) => string;
  };
  typography: {
    fontFamily: string;
    fontSize: number;
    fontWeightLight: number;
    fontWeightRegular: number;
    fontWeightMedium: number;
    fontWeightBold: number;
    h1: any;
    h2: any;
    h3: any;
    h4: any;
    h5: any;
    h6: any;
    subtitle1: any;
    subtitle2: any;
    body1: any;
    body2: any;
    button: any;
    caption: any;
    overline: any;
  };
  shadows: string[];
  transitions: {
    easing: {
      easeInOut: string;
      easeOut: string;
      easeIn: string;
      sharp: string;
    };
    duration: {
      shortest: number;
      shorter: number;
      short: number;
      standard: number;
      complex: number;
      enteringScreen: number;
      leavingScreen: number;
    };
  };
  zIndex: {
    mobileStepper: number;
    fab: number;
    speedDial: number;
    appBar: number;
    drawer: number;
    modal: number;
    snackbar: number;
    tooltip: number;
  };
  customization?: any;
}

/**
 * Create a minimal theme that bypasses all emotion processing
 */
export const createBypassTheme = (config: ThemeConfig = {}): BypassTheme => {
  const baseTheme: BypassTheme = {
    palette: {
      mode: config.palette?.mode || 'light',
      primary: {
        main: config.palette?.primary?.main || '#5e35b1',
        light: config.palette?.primary?.light || '#9575cd',
        dark: config.palette?.primary?.dark || '#4527a0',
        contrastText: '#ffffff'
      },
      secondary: {
        main: config.palette?.secondary?.main || '#ff6f00',
        light: config.palette?.secondary?.light || '#ffb74d',
        dark: config.palette?.secondary?.dark || '#e65100',
        contrastText: '#ffffff'
      },
      error: {
        main: '#ef4444',
        light: '#fef2f2',
        dark: '#dc2626',
        contrastText: '#ffffff'
      },
      warning: {
        main: '#f59e0b',
        light: '#fffbeb',
        dark: '#d97706',
        contrastText: '#ffffff'
      },
      success: {
        main: '#10b981',
        light: '#f0fdf4',
        dark: '#059669',
        contrastText: '#ffffff'
      },
      info: {
        main: '#3b82f6',
        light: '#eff6ff',
        dark: '#2563eb',
        contrastText: '#ffffff'
      },
      background: {
        default: '#f8fafc',
        paper: '#ffffff'
      },
      text: {
        primary: '#1e293b',
        secondary: '#64748b'
      },
      divider: '#e2e8f0',
      action: {
        hover: 'rgba(0, 0, 0, 0.04)',
        selected: 'rgba(0, 0, 0, 0.08)'
      },
      grey: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#eeeeee',
        300: '#e0e0e0',
        400: '#bdbdbd',
        500: '#9e9e9e',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121'
      }
    },
    spacing: (factor) => `${8 * factor}px`,
    shape: {
      borderRadius: 8
    },
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'xl'],
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536
      },
      up: (key) => `@media (min-width:${baseTheme.breakpoints.values[key]}px)`,
      down: (key) => {
        const value = baseTheme.breakpoints.values[key];
        return `@media (max-width:${value - 0.05}px)`;
      },
      between: (start, end) => {
        const startValue = baseTheme.breakpoints.values[start];
        const endValue = baseTheme.breakpoints.values[end];
        return `@media (min-width:${startValue}px) and (max-width:${endValue - 0.05}px)`;
      },
      only: (key) => {
        const keys = ['xs', 'sm', 'md', 'lg', 'xl'];
        const index = keys.indexOf(key);
        if (index === keys.length - 1) {
          return baseTheme.breakpoints.up(key);
        } else {
          return baseTheme.breakpoints.between(key, keys[index + 1]);
        }
      }
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: '2.125rem',
        fontWeight: 300,
        lineHeight: 1.167
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: 400,
        lineHeight: 1.2
      },
      h3: {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: 1.167
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.235
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.334
      },
      h6: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.6
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.75
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.57
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.43
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.75,
        textTransform: 'uppercase'
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.66
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 2.66,
        textTransform: 'uppercase'
      }
    },
    shadows: [
      'none',
      '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
      '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)'
    ],
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)'
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195
      }
    },
    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: 1100,
      drawer: 1200,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500
    }
  };

  // Merge with custom config
  if (config.customization) {
    baseTheme.customization = config.customization;
  }

  return baseTheme;
};

export default createBypassTheme;
